/**
 * Product Card Quantity Synchronization
 * Hooks into Dawn's native cart events AND observes the drawer DOM
 * to keep collection page cards in sync with the cart at all times.
 */
(function () {
  if (window.CardQtySyncInitialized) return;

  // ─── Debounce helper ─────────────────────────────────────────────────────────
  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  // ─── Core Sync Function ──────────────────────────────────────────────────────
  // Runs on EVERY cart update, over every card on the page — 352 of them on the
  // homepage. It is the single hottest interaction path in the theme, so it is
  // written to touch the DOM only where something actually changed:
  //   • one Map lookup per card instead of a linear cart.items.find()
  //   • a `data-qsync` stamp so unchanged cards are skipped entirely — no class
  //     writes, no querySelector, no validateQtyRules(). A class write on a card
  //     invalidates style for its subtree, so skipping is what keeps the
  //     interaction off the critical path.
  //   • validateQtyRules() only when the value really moved (it reads layout)
  window.updateAllCardState = function (cart) {
    if (!cart || !cart.items) return;

    const qtyByVariant = new Map();
    for (const i of cart.items) qtyByVariant.set(String(i.variant_id), i.quantity);

    document.querySelectorAll('.card__quantity-selector').forEach((selector) => {
      const variantId = selector.dataset.variantId;
      if (!variantId) return;

      const qty = qtyByVariant.get(String(variantId)) || 0;

      // Nothing changed for this card since the last sync — leave it alone.
      if (selector.dataset.qsync === String(qty)) return;
      selector.dataset.qsync = String(qty);

      const quantityInput = selector.querySelector('.quantity__input');
      const isInCart = qty > 0;

      if (quantityInput) {
        const minQty = parseInt(quantityInput.dataset.min) || 1;
        const currentVal = parseInt(quantityInput.value);
        const targetVal = qty === 0 ? minQty : qty;

        if (currentVal !== targetVal) {
          quantityInput.value = targetVal;
          const hiddenQuantityInput = selector.querySelector('.product-form-quantity');
          if (hiddenQuantityInput) hiddenQuantityInput.value = targetVal;

          const qtyInputComponent = quantityInput.closest('quantity-input');
          if (qtyInputComponent && typeof qtyInputComponent.validateQtyRules === 'function') {
            qtyInputComponent.validateQtyRules();
          }
        }
      }

      selector.classList.toggle('has-cart', isInCart);
      const cardWrapper = selector.closest('.card-wrapper') || selector.closest('.card');
      if (cardWrapper) cardWrapper.classList.toggle('has-cart', isInCart);

      if (!isInCart) {
        const submitButton = selector.querySelector('.product-form__cart-submit');
        if (submitButton && submitButton.dataset.available !== 'false') {  // sold-out cards stay disabled
          submitButton.removeAttribute('aria-disabled');
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');
          const spinner = selector.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
          const buttonText = submitButton.querySelector('span');
          if (buttonText && buttonText.dataset.originalText) {
            buttonText.textContent = buttonText.dataset.originalText;
          }
        }
      }
    });
  };

  // ─── Fetch cart and sync ─────────────────────────────────────────────────────
  const fetchAndSync = debounce(function () {
    fetch(window.routes.cart_url + '.js')
      .then((r) => r.json())
      .then(window.updateAllCardState)
      .catch(() => { });
  }, 300);

  // ─── Open Cart Drawer ────────────────────────────────────────────────────────
  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }
  // ─── Global Cart Change Helper ───────────────────────────────────────────────
  // Card +/- → the cart. Dawbu's side cart is custom (no <cart-drawer-items>), so the
  // old version changed the cart but never redrew the drawer, header bag, sticky dock
  // or subtotal, and sent no cart:updated — the card number moved, nothing else did.
  // Now: taps are coalesced per variant (only the final quantity is sent, so rapid
  // taps can't race), the change targets the cart LINE KEY (a variant id can match a
  // gift/properties line first), the drawer + bubble are redrawn from the returned
  // sections, and cart:updated tells the dock / bottom bar / tracking.
  const pendingQty = new Map();
  window.updateCartQuantityGlobal = function (variantId, quantity) {
    const vid = String(variantId);
    const prev = pendingQty.get(vid);
    if (prev) clearTimeout(prev.timer);
    pendingQty.set(vid, { quantity, timer: setTimeout(() => sendQty(vid), 350) });
  };

  function sendQty(vid) {
    const job = pendingQty.get(vid);
    pendingQty.delete(vid);
    if (!job) return;
    fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const lines = cart.items.filter((i) => String(i.variant_id) === vid);
        const line = lines.find((i) => !(i.properties && i.properties._gift === 'true')) || lines[0];
        if (!line) { window.updateAllCardState(cart); return null; }   // removed elsewhere meanwhile
        if (line.quantity === job.quantity) return null;
        return fetch(window.routes.cart_change_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: line.key, quantity: job.quantity, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname }),
        })
          .then((r) => r.json().then((state) => { if (!r.ok || state.status) throw new Error(state.description || state.message || r.status); return state; }));
      })
      .then((state) => {
        if (!state) return;
        if (state.sections && window.mzApplyCartSections) window.mzApplyCartSections(state.sections);
        window.updateAllCardState(state);
        if (window.publish && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'card-qty-sync', cart: state, cartData: state });
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'card-qty' } }));
        if (window.mzReconcileGifts) window.mzReconcileGifts();   // a card + can cross a free-gift tier
      })
      .catch((e) => {
        console.error('[card-qty-sync] Cart change failed:', e);
        // Put the card back to what the cart really holds
        document.querySelectorAll('.card__quantity-selector[data-variant-id="' + vid + '"]').forEach((sel) => { delete sel.dataset.qsync; });
        fetchAndSync();
      });
  }

  // ─── Observe the cart drawer for DOM changes ─────────────────────────────────
  // This is the most reliable hook: whenever the drawer re-renders after a
  // quantity change (replaceWith / innerHTML swap), we fetch the fresh cart.
  function watchDrawer() {
    const drawerTarget = document.querySelector('#CartDrawer-CartItems') || document.querySelector('cart-drawer-items');
    if (!drawerTarget) return;

    const drawerObserver = new MutationObserver(debounce(() => {
      fetchAndSync();
    }, 400));

    drawerObserver.observe(drawerTarget, { childList: true, subtree: true, characterData: true });
  }

  // ─── Initialise ─────────────────────────────────────────────────────────────
  const initSync = () => {
    if (window.CardQtySyncInitialized) return;

    // PUB_SUB_EVENTS is a module-scoped global in Dawn — NOT on window.
    // Use typeof to detect it reliably regardless of how it's exposed.
    const _subscribe = window.subscribe || (typeof subscribe !== 'undefined' ? subscribe : null);
    const _events = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);

    if (_subscribe && _events) {
      window.CardQtySyncInitialized = true;
      // Expose for debugging
      window.PUB_SUB_EVENTS = _events;

      try {
        // Hook 1: Dawn's PubSub event (fires on add-to-cart, cart-items changes)
        _subscribe(_events.cartUpdate, (event) => {
          const cart = event.cart || event.cartData;
          if (cart && cart.items) {
            window.updateAllCardState(cart);
          } else {
            fetchAndSync();
          }
        });
      } catch (e) {
        console.warn('[card-qty-sync] PubSub subscribe failed, falling back to DOM observer only.', e);
      }

      // Hook 2: Watch the drawer DOM — catches qty changes that don't fire PubSub
      watchDrawer();

      // Re-attach drawer watcher after the drawer itself re-renders
      // (cart-drawer-items gets replaceWith() which destroys the observer target)
      const drawerContainer = document.querySelector('#CartDrawer') || document.querySelector('cart-drawer');
      if (drawerContainer) {
        new MutationObserver(() => {
          watchDrawer();
        }).observe(drawerContainer, { childList: true, subtree: false });
      }

      // Initial sync on page load
      fetchAndSync();
    } else {
      setTimeout(initSync, 50);
    }
  };

  // ─── Product Card Quantity Input Changes ─────────────────────────────────────
  document.addEventListener('change', (event) => {
    const input = event.target.closest('.card__quantity-selector .quantity__input');
    if (!input) return;

    const selector = input.closest('.card__quantity-selector');
    if (!selector.classList.contains('has-cart')) return;

    const variantId = selector.dataset.variantId;
    const newQty = parseInt(input.value);
    if (!variantId || isNaN(newQty)) return;

    window.updateCartQuantityGlobal(variantId, newQty);
    // Cart opening is handled solely by product-form.js (matches the working
    // reference theme). Do not open the drawer on a quantity-stepper change.
  });

  // ─── Add-to-Cart Form Submission Feedback ────────────────────────────────────
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-type="add-to-cart-form"]');
    if (!form || !form.closest('.card__quantity-selector')) return;

    const selector = form.closest('.card__quantity-selector');
    selector.classList.add('has-cart');
    const cardWrapper = selector.closest('.card-wrapper') || selector.closest('.card');
    if (cardWrapper) cardWrapper.classList.add('has-cart');

    const submitButton = selector.querySelector('.product-form__cart-submit');
    if (submitButton) {
      const buttonText = submitButton.querySelector('span');
      if (buttonText && !buttonText.dataset.originalText) {
        buttonText.dataset.originalText = buttonText.textContent;
      }
    }

    // Cart opening is handled solely by product-form.js (matches the working
    // reference theme, which has no card-qty-sync.js). This handler only keeps
    // the card's has-cart UI state in sync; it no longer opens the drawer.
  });

  // ─── Cart changed anywhere else (side cart +/-/remove, gift wrap, upsells) ────
  // Dawbu's side cart announces changes with cart:updated, not Dawn's PubSub, so
  // without this a card stayed on [- 1 +] after its item was removed in the drawer.
  document.addEventListener('cart:updated', (e) => {
    if (e.detail && e.detail.source === 'card-qty') return;   // our own change already synced the cards
    fetchAndSync();
  });
  // Back button: a page restored from the back/forward cache still shows the old cart
  window.addEventListener('pageshow', (e) => { if (e.persisted) fetchAndSync(); });

  // ─── Visibility Sync ─────────────────────────────────────────────────────────
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      fetchAndSync();
    }
  });

  // ─── Infinite Scroll / Filter Observer ──────────────────────────────────────
  const gridObserver = new MutationObserver(debounce((mutations) => {
    let hasNewCards = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (
          node.nodeType === 1 &&
          (node.querySelector('.card__quantity-selector') || node.classList.contains('card__quantity-selector'))
        ) {
          hasNewCards = true;
          break;
        }
      }
      if (hasNewCards) break;
    }
    if (hasNewCards) fetchAndSync();
  }, 300));

  const productGrid = document.getElementById('product-grid') || document.querySelector('.product-grid');
  // Only observe an actual product grid. NEVER fall back to document.body: on the
  // homepage there is no #product-grid, so observing the whole body subtree made
  // every DOM mutation (Swiper autoplay/transitions fire constantly) run this
  // observer + fetchAndSync, locking up the main thread and making the whole site
  // (taps, hamburger, add-to-cart) feel unresponsive.
  if (productGrid) {
    gridObserver.observe(productGrid, { childList: true, subtree: true });
  }

  // ─── Boot ────────────────────────────────────────────────────────────────────
  initSync();
})();
